import axios from '../../../api/axios';
import React, { useEffect, useState, useRef } from "react";
import styled from 'styled-components';
import ChatHistoryMessage from './ChatHistoryMessage';
import ChatHistoryHeader from './ChatHistoryHeader';

interface ChatProps {
  userId: string | null;
  username: string | null;
  conversationID: string;
  conversationType: number;
  profileName: string;
  teleconsults: any[];
}

const CenteredContainer = styled.div`
  display: flex;
  justify-content: center;
  background-color: #f0f2f5;
`;

const ChatBox = styled.div`
  width: calc(100vw - 32px);
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  font-family: Arial, sans-serif;
  overflow: hidden;
  margin: 0 auto 20px auto;
`;

const UserHeader = styled.div`
  padding: 10px 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  background-color: #fff;
`;

const ProfileText = styled.div`
  font-weight: bold;
  font-size: 14px;
  color: #333;

  span {
    color: #999;
    font-weight: normal;
    margin-left: 5px;
  }
`;

const ChatSection = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fafafa;
`;

const SearchInput = styled.input`
  width: calc(100% - 32px);
  margin: 12px 16px;
  border: 1px solid #ccc;
  border-radius: 6px;
  outline: none;
`;

const SectionTitle = styled.div`
  color: #00bfa5;
  font-weight: bold;
  padding: 0px 16px;
  font-size: 14px;
  background: #f5f5f5;
  border-bottom: 1px solid #ddd;
`;

const ChatItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0px 16px;
  border-bottom: 1px solid #eee;
  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const ChatAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 12px;
`;

const ChatContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ChatName = styled.span`
  font-weight: bold;
  font-size: 14px;
`;

const ChatDate = styled.span`
  font-size: 12px;
  color: #888;
`;

const MessagesContainer = styled.div`
  flex: 1;
  max-height: 60vh;
  min-height: 60vh;
  padding: 0 16px 16px;
  overflow-y: auto;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
`;

const ChatHistoryList: React.FC<ChatProps> = ({ userId, username, conversationID, conversationType, profileName, teleconsults }) => {
  interface Chat {
    id: string | number;
    name: string;
    date: string;
    avatar: string;
    roomId: number;
    specialty: string;
  }
    
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [messages, setMessages] = useState<Array<{
    message: string;
    time?: number;
    type?: string;
    timestamp: string;
    isDoctor: boolean;
    imageUrl?: string;
    fileName?: string;
  }>>([]);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  const loadMessagesFromLocalStorage = () => {
    const savedMessages = localStorage.getItem('ChatHistoryMessages');
    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    }
  };

  useEffect(() => {
    loadMessagesFromLocalStorage();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const storedData = sessionStorage.getItem('userData');
  const userData = storedData ? JSON.parse(storedData) : null;
  const token = userData ? userData.token : null;

  const fetchChats = async (roomId: number) => {
    if (!token) return;
  
    try {
      const response = await axios.get(`chat-histories/${roomId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      const chatData = response.data;
      const rawMessages = chatData.data?.List || [];
  
      const currentTeleconsult = teleconsults.find(tc => tc.room_id === roomId);
      const patientSenderId = currentTeleconsult ? `LMS${currentTeleconsult.patient_id}local` : null;
      
      const formattedMessages = rawMessages
      .map((item: any) => {
        const msg = item.List;
        let parsedMsgBody;
        
        try {
          parsedMsgBody = JSON.parse(msg.MsgBody);
        } catch (err) {
          console.warn('Failed to parse MsgBody:', msg.MsgBody);
          return null;
        }

        if (!parsedMsgBody.message) return null;

        return {
          message: parsedMsgBody.message,
          time: parsedMsgBody.time,
          type: parsedMsgBody.type,
          timestamp: new Date(msg.MsgTime).toLocaleTimeString('id-ID', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }),
          isDoctor: msg.Sender !== patientSenderId,
          imageUrl: parsedMsgBody.imageUrl,
          fileName: parsedMsgBody.fileName,
        };
      })
      .filter(Boolean);
  
      setMessages(formattedMessages);
    } catch (error) {
      console.error('Error fetching chats:', error);
    }
  };  

  const formatDate = (isoDate: string) => {
    return new Intl.DateTimeFormat('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(isoDate));
  };
  
  const chats: Chat[] = Array.isArray(teleconsults)
  ? [...teleconsults]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .map((item: any) => ({
        id: item.id,
        name: item.practitioner?.name || 'Tidak diketahui',
        date: formatDate(item.created_at),
        avatar: item.practitioner?.avatar?.url || 'https://cdn-icons-png.flaticon.com/512/4140/4140051.png',
        roomId: item.room_id,
        specialty: item.practitioner?.specialty?.name || 'Dokter',
      }))
  : [];

  const filteredChats = chats.filter(chat =>
    chat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );  

  return (
    <CenteredContainer>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        margin: '12px 16px 52px 16px'
      }}>
        {profileName?.trim() && (
          <UserHeader>
            <ProfileText>
              {profileName} <span>(Me)</span>
            </ProfileText>
          </UserHeader>
        )}

        <ChatBox>
          <ChatSection>
            {!selectedChat && (
              <>
                <SearchInput
                  type="text"
                  placeholder="Cari percakapan"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <SectionTitle>Chat Selesai</SectionTitle>
                {filteredChats.length > 0 ? (
                  filteredChats.map((chat, index) => (
                    <ChatItem
                      key={index}
                      onClick={() => {
                        setSelectedChat(chat);
                        fetchChats(chat.roomId);
                      }}
                    >
                      <ChatAvatar src={chat.avatar} alt={chat.name} />
                      <ChatContent>
                        <ChatName>{chat.name}</ChatName>
                        <ChatDate>{chat.date}</ChatDate>
                      </ChatContent>
                    </ChatItem>
                  ))
                ) : (
                  <div style={{ padding: '16px', color: '#666', textAlign: 'center' }}>
                    Tidak ada percakapan yang cocok
                  </div>
                )}
              </>
            )}

            {selectedChat && (
              <>
                  <ChatHistoryHeader
                    onBack={() => setSelectedChat(null)}
                    chatName={selectedChat.name}
                    chatAvatar={selectedChat.avatar}
                    chatRoomId={selectedChat.roomId}
                    chatSpeciality={selectedChat.specialty}
                  />
                  <MessagesContainer ref={messagesContainerRef}>
                    {messages.map((msg, index) => (
                      <ChatHistoryMessage key={index} {...msg} />
                    ))}
                    <div ref={messagesEndRef} />
                  </MessagesContainer>
              </>
            )}
          </ChatSection>
        </ChatBox>
      </div>
    </CenteredContainer>
  );
};

export default ChatHistoryList;
